/**
 * SEO Component
 * Handles dynamic meta tags, hreflang, and Open Graph for each page
 * Uses the translation system for multi-language SEO content
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { LANGUAGES } from '../../config/languages';
import {
  LANGUAGE_LOCALES,
  BASE_URL,
  SEO_FALLBACKS,
  getSeoKey,
  type PageKey,
} from '../../config/seo';

interface SEOProps {
  page: PageKey;
  /** Optional custom title override (for dynamic pages) */
  customTitle?: string;
  /** Optional custom description override (for dynamic pages) */
  customDescription?: string;
}

// Build URL with language parameter
function buildLangUrl(path: string, lang: string): string {
  const cleanPath = path.split('?')[0]; // Remove existing query params
  if (lang === 'en') {
    return `${BASE_URL}${cleanPath}`;
  }
  return `${BASE_URL}${cleanPath}?lang=${lang}`;
}

// Helper to set or update a meta tag
function setMetaTag(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let element = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, name);
    document.head.appendChild(element);
  }

  element.content = content;
}

// Helper to set or update a link tag
function setLinkTag(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;

  let element = document.querySelector(selector) as HTMLLinkElement;

  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    if (hreflang) {
      element.hreflang = hreflang;
    }
    document.head.appendChild(element);
  }

  element.href = href;
}

export function SEO({ page, customTitle, customDescription }: SEOProps) {
  const location = useLocation();
  const { language, t } = useLanguage();

  // Get translated SEO content, falling back to static English
  const fallback = SEO_FALLBACKS[page];
  const title = customTitle || t(getSeoKey(page, 'title'), fallback.title);
  const description = customDescription || t(getSeoKey(page, 'description'), fallback.description);

  const canonicalUrl = buildLangUrl(location.pathname, language);
  const ogLocale = LANGUAGE_LOCALES[language] || 'en_US';

  useEffect(() => {
    // Set document language
    document.documentElement.lang = language;

    // Set title
    document.title = title;

    // Primary Meta Tags
    setMetaTag('title', title);
    setMetaTag('description', description);

    // Canonical URL
    setLinkTag('canonical', canonicalUrl);

    // Open Graph / Facebook
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:image', `${BASE_URL}/og-image.png`, true);
    setMetaTag('og:locale', ogLocale, true);

    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image', true);
    setMetaTag('twitter:url', canonicalUrl, true);
    setMetaTag('twitter:title', title, true);
    setMetaTag('twitter:description', description, true);
    setMetaTag('twitter:image', `${BASE_URL}/og-image.png`, true);

    // Hreflang Tags for International SEO - all supported languages
    LANGUAGES.forEach(lang => {
      setLinkTag('alternate', buildLangUrl(location.pathname, lang.code), lang.code);
    });

    // x-default for users without language preference
    setLinkTag('alternate', `${BASE_URL}${location.pathname.split('?')[0]}`, 'x-default');

  }, [language, title, description, canonicalUrl, ogLocale, location.pathname]);

  // This component doesn't render anything visible
  return null;
}

export default SEO;
